import React from "react";
import { Button, Form, Modal, Table } from "react-bootstrap";

const MAX_COMPARED = 4;

/**
 * Side-by-side view of one night's stays - the same facts the hotel list shows, lined up in columns
 * so they can be read across. Everything here is already on the page for that night (price, rooms,
 * meals, facilities, stay type); nothing that identifies a hotel is added.
 */
export default function CompareStaysModal({
  show,
  night,
  options,
  value,
  formatAmount,
  roomTypesFor,
  priceRoomType,
  onChoose,
  onHide,
}) {
  const [picked, setPicked] = React.useState([]);
  // Per stay: the room type picked here and its repriced figures. Empty = the suggested rooms.
  const [roomChoices, setRoomChoices] = React.useState({});

  React.useEffect(() => {
    if (show) setRoomChoices({});
  }, [show, options]);

  const suggestedCategoryId = (option) => String(option.price?.roomLines?.[0]?.roomCategoryId ?? "");

  const changeRoomType = async (option, categoryId) => {
    const key = String(option.value);
    if (!categoryId || categoryId === suggestedCategoryId(option)) {
      setRoomChoices((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
      return;
    }
    setRoomChoices((current) => ({ ...current, [key]: { categoryId, loading: true } }));
    try {
      const priced = await priceRoomType(option.value, categoryId);
      setRoomChoices((current) =>
        current[key]?.categoryId === categoryId
          ? { ...current, [key]: { categoryId, loading: false, priced } }
          : current,
      );
    } catch (error) {
      setRoomChoices((current) =>
        current[key]?.categoryId === categoryId
          ? {
              ...current,
              [key]: {
                categoryId,
                loading: false,
                error: error?.response?.data?.message || "Could not price this room type",
              },
            }
          : current,
      );
    }
  };

  // Start from the current stay, then Iotrips picks, then the cheapest - up to four columns.
  React.useEffect(() => {
    if (!show) return;
    const choosable = (options || []).filter((option) => !option.disabled);
    const current = choosable.filter((option) => String(option.value) === String(value));
    const rest = choosable
      .filter((option) => String(option.value) !== String(value))
      .sort((left, right) => Number(Boolean(right.iotripsPick)) - Number(Boolean(left.iotripsPick)));
    setPicked([...current, ...rest].slice(0, MAX_COMPARED).map((option) => String(option.value)));
  }, [show, options, value]);

  const columns = (options || []).filter((option) => picked.includes(String(option.value)));

  const toggle = (optionValue) => {
    const key = String(optionValue);
    setPicked((current) => {
      if (current.includes(key)) return current.filter((item) => item !== key);
      if (current.length >= MAX_COMPARED) return current;
      return [...current, key];
    });
  };

  const choiceFor = (option) => roomChoices[String(option.value)];
  const pricedFor = (option) => choiceFor(option)?.priced;

  const differenceText = (difference, suffix) => {
    if (Math.round(difference) === 0) return "Same price";
    return difference < 0
      ? `Rs. ${formatAmount(Math.abs(Math.round(difference)))} less${suffix}`
      : `Rs. ${formatAmount(Math.round(difference))} more${suffix}`;
  };

  const priceText = (option) => {
    const choice = choiceFor(option);
    if (choice?.loading) return "Working out...";
    if (choice?.error) return <span className="text-danger">{choice.error}</span>;
    if (choice?.priced) return differenceText(Number(choice.priced.perPersonDifference), " pp");
    const price = option.price;
    if (!price || price.unavailable || price.perPersonPrice == null) return "Price on request";
    if (price.selected) return "Current choice";
    return differenceText(Number(price.perPersonDifference ?? 0), " pp");
  };

  const totalText = (option) => {
    const priced = pricedFor(option);
    const difference = Number(priced ? priced.totalDifference : option.totalDifference);
    if ((!priced && option.price?.selected) || !Number.isFinite(difference) || Math.round(difference) === 0) return "-";
    return differenceText(difference, "");
  };

  const roomsCell = (option) => {
    const roomTypes = roomTypesFor ? roomTypesFor(option.value) : [];
    const choice = choiceFor(option);
    const roomsText = pricedFor(option)?.roomsText || option.roomsText || option.price?.roomSummary || "-";
    if (roomTypes.length <= 1) return roomsText;
    return (
      <>
        <Form.Select
          size="sm"
          className="mb-1"
          value={choice?.categoryId || suggestedCategoryId(option)}
          disabled={option.disabled || choice?.loading}
          onChange={(event) => changeRoomType(option, event.target.value)}
          aria-label={`Room type for ${option.label}`}
        >
          {!suggestedCategoryId(option) && <option value="">Suggested rooms</option>}
          {roomTypes.map((roomType) => (
            <option key={roomType.id} value={roomType.id} disabled={roomType.soldOut}>
              {roomType.name}
              {roomType.sleeps > 1 ? ` (sleeps ${roomType.sleeps})` : ""}
              {roomType.soldOut ? " - sold out" : roomType.left <= 2 ? ` - only ${roomType.left} left` : ""}
            </option>
          ))}
        </Form.Select>
        <div>{roomsText}</div>
      </>
    );
  };

  const rows = [
    { label: "Price per person", render: (option) => <strong>{priceText(option)}</strong> },
    { label: "Total for the group", render: totalText },
    { label: "Rooms", render: roomsCell },
    {
      label: "Meals",
      render: (option) => {
        const mealNote = pricedFor(option) ? pricedFor(option).mealNote : option.mealNote;
        return (
          <>
            {option.mealPlan && (
              <span className={`hotel-select__chip is-${option.mealPlan.tone}`}>{option.mealPlan.label}</span>
            )}
            {mealNote && <div className="hotel-select__meal-note">{mealNote}</div>}
          </>
        );
      },
    },
    { label: "Type of stay", render: (option) => option.stayType || "-" },
    {
      label: "Included",
      render: (option) => ((option.chips || []).length ? option.chips.join(", ") : "-"),
    },
    {
      label: "At extra cost",
      render: (option) => ((option.extras || []).length ? option.extras.join(", ") : "-"),
    },
    {
      label: "Availability",
      render: (option) => {
        if (option.soldOut) return <span className="text-danger">Sold out</span>;
        if (option.roomsLeft > 0 && option.roomsLeft <= 2) {
          return <span className="text-danger fw-semibold">Only {option.roomsLeft} left</span>;
        }
        return "Available";
      },
    },
    {
      label: "",
      render: (option) => (
        <div className="d-flex flex-wrap gap-1">
          {option.iotripsPick && <span className="hotel-select__chip is-pick">★ Iotrips pick</span>}
          {option.price?.cheapest && <span className="hotel-select__chip is-lowest">Lowest price</span>}
        </div>
      ),
    },
  ];

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="h6 mb-0">Compare stays - Night {night}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="small text-muted mb-1">Choose up to {MAX_COMPARED} stays to compare</div>
        <div className="d-flex flex-wrap gap-2 mb-3">
          {(options || []).map((option) => {
            const key = String(option.value);
            const checked = picked.includes(key);
            return (
              <Form.Check
                key={key}
                type="checkbox"
                id={`compare-stay-${night}-${key}`}
                className="small border rounded px-2 py-1 ms-0"
                label={option.label}
                checked={checked}
                disabled={option.disabled || (!checked && picked.length >= MAX_COMPARED)}
                onChange={() => toggle(option.value)}
              />
            );
          })}
        </div>
        {columns.length === 0 ? (
          <div className="small text-muted">Tick at least one stay above.</div>
        ) : (
          <Table responsive bordered size="sm" className="small align-top mb-0 compare-stays-table">
            <thead>
              <tr>
                <th style={{ width: 130 }} />
                {columns.map((option) => (
                  <th key={option.value} className={String(option.value) === String(value) ? "table-primary" : ""}>
                    {option.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={row.label || `row-${rowIndex}`}>
                  <th className="text-muted fw-normal">{row.label}</th>
                  {columns.map((option) => (
                    <td key={option.value}>{row.render(option)}</td>
                  ))}
                </tr>
              ))}
              <tr>
                <th />
                {columns.map((option) => {
                  const isCurrent = String(option.value) === String(value);
                  const choice = choiceFor(option);
                  const roomChoice = choice?.priced
                    ? { categoryId: choice.categoryId, units: choice.priced.units }
                    : null;
                  // The current stay with a different room type picked here is still a change to apply.
                  const nothingToApply = isCurrent && !roomChoice;
                  return (
                    <td key={option.value}>
                      <Button
                        type="button"
                        size="sm"
                        variant={nothingToApply ? "outline-secondary" : "primary"}
                        disabled={nothingToApply || option.soldOut || choice?.loading || Boolean(choice?.error)}
                        onClick={() => onChoose(option.value, roomChoice)}
                      >
                        {nothingToApply ? "Current choice" : isCurrent ? "Use this room type" : "Choose this stay"}
                      </Button>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </Table>
        )}
      </Modal.Body>
    </Modal>
  );
}
